import template from './xps-product-finder-detail.html.twig';

const { Component, Mixin } = Shopware;
const { Criteria } = Shopware.Data;
const { mapPropertyErrors } = Component.getComponentHelper();

Component.register('xps-product-finder-detail', {
    template,

    inject: [
        'repositoryFactory'
    ],

    mixins: [
        Mixin.getByName('notification'),
        Mixin.getByName('placeholder')
    ],

    shortcuts: {
        'SYSTEMKEY+S': 'onSave',
        ESCAPE: 'onCancel'
    },

    data() {
        return {
            productFinder: null,
            isLoading: false,
            processSuccess: false,
            activeStepId: null,
            removedStepIds: [], // Array to track removed step IDs
            removedOptionIds: [] // Array to track removed option IDs
        };
    },

    metaInfo() {
        return {
            title: this.$tc('xps-product-finder.detail.title')
        };
    },

    computed: {
        productCriteria() {
            const criteria = new Criteria();
            criteria.addAssociation('manufacturer');
            criteria.addAssociation('cover');
            criteria.addFilter(Criteria.equals('active', true));
            criteria.setLimit(25);
            return criteria;
        },

        productRepository() {
            return this.repositoryFactory.create('product');
        },

        repository() {
            return this.repositoryFactory.create('xps_product_finder');
        },

        stepRepository() {
            return this.repositoryFactory.create('xps_product_finder_step');
        },

        optionRepository() {
            return this.repositoryFactory.create('xps_product_finder_option');
        },

        productFinderId() {
            return this.$route.params.id;
        },

        isCreateMode() {
            return this.$route.name === 'xps.product.finder.create';
        },

        ...mapPropertyErrors('productFinder', [
            'name'
        ])
    },

    created() {
        this.createdComponent();
    },

    methods: {
        createdComponent() {
            if (this.isCreateMode) {
                this.productFinder = this.repository.create(Shopware.Context.api);
                this.productFinder.active = true;
                this.productFinder.position = 0;
            } else {
                this.getProductFinder();
            }
        },

        getProductFinder() {
            const criteria = new Criteria();
            criteria.addAssociation('steps.options');
            criteria.getAssociation('steps').addSorting(Criteria.sort('position'));
            criteria.getAssociation('steps.options').addSorting(Criteria.sort('position'));

            this.repository.get(this.productFinderId, Shopware.Context.api, criteria)
                .then((entity) => {
                    this.productFinder = entity;
                    // Reset removed arrays when loading fresh data
                    this.removedStepIds = [];
                    this.removedOptionIds = [];
                    
                    // Ensure steps collection is properly initialized
                    if (this.productFinder.steps && !this.productFinder.steps.add) {
                        this.productFinder.steps = this.stepRepository.create(Shopware.Context.api, this.productFinder.steps);
                    }
                    
                    // Ensure options collections are properly initialized for each step
                    if (this.productFinder.steps) {
                        this.productFinder.steps.forEach(step => {
                            if (step.options && !step.options.add) {
                                step.options = this.optionRepository.create(Shopware.Context.api, step.options);
                            }

                            // Guarantee productIds is always a real array. Options loaded from
                            // the database have product_ids = NULL, and passing null to
                            // sw-entity-multi-id-select makes it run an unfiltered search and
                            // render every product as "selected", while also blocking edits
                            // from being written back to the entity (so removals never save).
                            if (step.options) {
                                step.options.forEach(option => {
                                    if (!Array.isArray(option.productIds)) {
                                        option.productIds = [];
                                    }
                                });
                            }
                        });
                    }
                });
        },

        normalizeOptionProductIds() {
            this.productFinder?.steps?.forEach(step => {
                step.options?.forEach(option => {
                    if (!Array.isArray(option.productIds)) {
                        option.productIds = [];
                    }
                });
            });
        },

        async onSave() {
            this.isLoading = true;

            try {
                // Normalize any untouched options (null -> []) so an empty selection
                // is persisted as an empty array instead of being skipped.
                this.normalizeOptionProductIds();

                // Save the product finder first to handle any updates
                await this.repository.save(this.productFinder, Shopware.Context.api);
                
                // Then delete removed options and steps from database
                await this.deleteRemovedItems();
                
                // Clear the removed arrays after successful operations
                this.removedStepIds = [];
                this.removedOptionIds = [];
                
                // Show success state immediately
                this.processSuccess = true;
                this.isLoading = false;
                
                // Reload fresh data from database after a short delay
                setTimeout(() => {
                    this.getProductFinder();
                }, 100);
                
            } catch (exception) {
                this.isLoading = false;
                this.processSuccess = false;
                this.createNotificationError({
                    title: this.$tc('xps-product-finder.detail.errorTitle'),
                    message: exception
                });
            }
        },

        async deleteRemovedItems() {
            const deletePromises = [];

            // Delete removed options first (child entities)
            if (this.removedOptionIds.length > 0) {
                this.removedOptionIds.forEach(optionId => {
                    deletePromises.push(
                        this.optionRepository.delete(optionId, Shopware.Context.api)
                    );
                });
            }

            // Delete removed steps (parent entities)
            if (this.removedStepIds.length > 0) {
                this.removedStepIds.forEach(stepId => {
                    deletePromises.push(
                        this.stepRepository.delete(stepId, Shopware.Context.api)
                    );
                });
            }

            // Execute all deletions
            if (deletePromises.length > 0) {
                await Promise.all(deletePromises);
            }
        },

        onCancel() {
            this.$router.push({ name: 'xps.product.finder.list' });
        },

        saveFinish() {
            this.processSuccess = false;
        },

        addStep() {
            // Ensure productFinder has a steps collection
            if (!this.productFinder.steps) {
                this.productFinder.steps = this.stepRepository.create(Shopware.Context.api);
            }
            
            // If steps exists but doesn't have collection methods, reinitialize it
            if (this.productFinder.steps && !this.productFinder.steps.add) {
                this.productFinder.steps = this.stepRepository.create(Shopware.Context.api, this.productFinder.steps);
            }

            const newStep = this.stepRepository.create(Shopware.Context.api);
            newStep.productFinderId = this.productFinder.id;
            newStep.name = 'New Step';
            newStep.position = this.productFinder.steps.length;

            this.productFinder.steps.add(newStep);
        },

        removeStep(stepId) {
            const step = this.productFinder.steps.get(stepId);
            
            if (step) {
                // If the step exists in database (has ID), add to removal tracking
                if (step.id && !this.isCreateMode) {
                    this.removedStepIds.push(step.id);
                    
                    // Also track all options in this step for deletion
                    if (step.options && step.options.length > 0) {
                        step.options.forEach(option => {
                            if (option.id) {
                                this.removedOptionIds.push(option.id);
                            }
                        });
                    }
                }
                
                // Remove step from collection
                this.productFinder.steps.remove(stepId);
            }
        },

        addOption(step) {
            // Ensure the step has an options collection
            if (!step.options) {
                step.options = this.optionRepository.create(Shopware.Context.api);
            }
            
            // If options exists but doesn't have collection methods, reinitialize it
            if (step.options && !step.options.add) {
                step.options = this.optionRepository.create(Shopware.Context.api, step.options);
            }

            const newOption = this.optionRepository.create(Shopware.Context.api);
            newOption.stepId = step.id;
            newOption.name = 'New Option';
            newOption.position = step.options.length;
            
            // Initialize productIds as empty array
            newOption.productIds = [];

            step.options.add(newOption);
        },

        removeOption(step, optionId) {
            const option = step.options.get(optionId);
            
            if (option) {
                // If the option exists in database (has ID), add to removal tracking
                if (option.id && !this.isCreateMode) {
                    this.removedOptionIds.push(option.id);
                }
                
                // Remove option from collection
                step.options.remove(optionId);
            }
        }
    }
});